import { useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import { Button } from '@/components_deprecated'
import {
  addDays,
  formatISO,
  parseISO,
  setHours,
  setMilliseconds,
  setMinutes,
} from 'date-fns'
import type { TimesheetsModuleConfig } from '@/modules/companies'
import { Module, ModuleOptionsValue } from '@/modules/companies'
import { useEffect } from 'react'
import { useLocation } from 'react-router'
import styled from 'styled-components'
import {
  useBreadcrumbs,
  useModuleConfig,
  useNodes,
  usePermissions,
  useUser,
} from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import { getFullMinutes } from '@/utils/time'
import { useTimeEntryForm } from '../hooks'
import { useTimeEntryMutations } from '../mutations.hooks'
import { USER_TIME_ENTRIES_QUERY } from '../queries'
import type { TimeEntryFormValues } from '../types'
import type { TimesheetsSalaryWorkTime } from '../types.graphql'
import { TimeEntryForm } from './TimeEntryForm'

const SubmitButton = styled(Button)`
  white-space: nowrap;

  ${props => props.theme.media.mobile} {
    width: 100%;
  }
`

const SubmitField = styled.div`
  display: flex;
  justify-content: flex-end;

  div.error {
    display: flex;
    align-items: center;

    margin-right: 1rem;
    color: ${props => props.theme.colors.red};
  }

  ${props => props.theme.media.mobile} {
    flex-direction: column;

    div.error {
      justify-content: center;
      margin-right: 0;
      margin-bottom: 1rem;
    }
  }
`

interface TimeEntryFormProps {
  salaryWorkTime: TimesheetsSalaryWorkTime
}

export const TimeEntryFormAPI: React.FC<TimeEntryFormProps> = ({
  salaryWorkTime,
}) => {
  const user = useUser()
  const translations = useTranslate({
    newTimeEntry: 'timesheets.new-time-entry',
    createTimeEntry: 'timesheets.register-timeentry',

    success: 'timesheets.success-registration-timeentry',
    submit: 'common.submit',

    error: {
      create: 'timesheets.error-create-timeentry',
      startBeforeEnd: 'timesheets.error.start-before-end',
      required: 'common.required',
    },
    warning: {
      create: 'timesheets.warning.time-entry-exist-in-daterange',
    },
  })
  const { getModuleOptionStatus } = useModuleConfig<TimesheetsModuleConfig>(
    Module.TIMESHEETS
  )
  const { setOverrides, overrides } = useBreadcrumbs()

  const location = useLocation()
  const form = useTimeEntryForm(
    null,
    salaryWorkTime,
    location.state?.initialUser
  )
  const date = setMilliseconds(
    setHours(setMinutes(form.formValues.start, 0), 0),
    0
  )

  const { create } = useTimeEntryMutations()

  const { data } = useQuery(USER_TIME_ENTRIES_QUERY, {
    variables: {
      user: user.id,
      datetimeStartGt: date.toISOString(),
      datetimeStartLt: addDays(date, 1).toISOString(),
    },
  })

  const existingTimeEntries = useNodes(
    data?.allTimeEntries,
    entry => parseISO(entry.datetimeStart) < form.formValues.start
  )

  const overtimeCustomChoice = getModuleOptionStatus(
    ModuleOptionsValue.TIMESHEETS_OVERTIME_OPTIONS
  )

  const { hasPermissions } = usePermissions()

  const canSetSalaryCodeManually = hasPermissions(
    PERMISSIONS.timesheets.overrideTimeEntrySalaryCode,
    false
  )

  function handleSubmit(values: TimeEntryFormValues) {
    const workingMs =
      values.end.getTime() -
      values.start.getTime() +
      existingTimeEntries.reduce((acc, entry) => {
        const start = parseISO(entry.datetimeStart)
        const end = !entry.datetimeEnd
          ? new Date()
          : parseISO(entry.datetimeEnd)
        return acc + (end.getTime() - start.getTime())
      }, 0)

    const totalWorkingSeconds = workingMs / 1000
    void create({
      variables: {
        input: {
          user: values.userId,
          vehicle: values.vehicleId || null,
          datetimeStart: formatISO(values.start),
          datetimeEnd: formatISO(values.end),
          facility: values.facility,
          comment: values.comment,
          salaryCodeOvertime: values.overtimeSalaryCodeId,
          salaryCodeOrdinary: values.salaryCodeId,
          diet: values.diet,
          department: values.department,
          pause:
            salaryWorkTime.pauseThreshold < totalWorkingSeconds
              ? getFullMinutes(values.pause)
              : 0,
        },
      },
    })
  }

  useEffect(() => {
    if (overrides['register']) return
    form.validateForm()
    setOverrides({ register: translations.newTimeEntry })
  }, [form, overrides, setOverrides, translations.newTimeEntry])

  return (
    <>
      <TimeEntryForm
        form={form}
        overtimeCustomChoice={overtimeCustomChoice}
        salaryWorkTime={salaryWorkTime}
        existingTimeEntries={existingTimeEntries}
        canSetSalaryCodeManually={canSetSalaryCodeManually}
        onSubmit={handleSubmit}
      />

      <SubmitField>
        <SubmitButton
          width="200px"
          disabled={!form.formValid}
          onClick={() => handleSubmit(form.formValues)}
        >
          {translations.submit}
        </SubmitButton>
      </SubmitField>
    </>
  )
}
